import { memo, useMemo, useRef, useState } from "react";
import type {
  AgentAnnotationTarget,
  AnnotationGeometry,
} from "@/lib/agent-annotation";
import {
  annotationSketchSeed,
  annotationSketchStrokes,
} from "@/lib/annotation-sketch";
import { INK_ALPHA } from "@/lib/sketch-stroke/ink-style";
import { sketchStrokePaths } from "@/lib/sketch-stroke/sketch-mark";
import { outlineSvgPath } from "@/lib/sketch-stroke/stroke-outline";
import { useSketchReveal } from "./use-sketch-reveal";

const ARROW_HEAD_REVEAL_MS = 140;

type MarkPath = ReadonlyArray<{ x: number; y: number }>;

/** Keeps the previous path while a mirrored copy carries the same points in a
 * new array, so hydrated windows do not rebuild unchanged strokes. Agent
 * paths are short, so every point is compared. */
function useStablePath(path: MarkPath | undefined): MarkPath | undefined {
  const stable = useRef(path);
  const previous = stable.current;
  if (path !== previous) {
    const same =
      !!path &&
      !!previous &&
      path.length === previous.length &&
      path.every(
        (point, index) =>
          point.x === previous[index]!.x && point.y === previous[index]!.y,
      );
    if (!same) stable.current = path;
  }
  return stable.current;
}

interface SketchMarkSvgProps {
  target: AgentAnnotationTarget;
  /** Display-local box, inflated by the ink overflow. */
  geometry: AnnotationGeometry;
  ink: string;
  testId: string;
  visible: boolean;
  snap: boolean;
  onOpacitySettled: () => void;
}

/** One agent mark as filled, tapered pen strokes. */
export const SketchMarkSvg = memo(function SketchMarkSvg({
  target,
  geometry,
  ink,
  testId,
  visible,
  snap,
  onOpacitySettled,
}: SketchMarkSvgProps) {
  const { shape, width, height, padding, emphasis, strokeWidth } = target;
  const isPath = shape === "path" || shape === "polyline";
  const path = useStablePath(isPath ? target.path : undefined);
  const arrowEnd = isPath && !!target.arrowEnd;
  const seed = annotationSketchSeed(target);
  const strokes = useMemo(
    () =>
      annotationSketchStrokes({
        shape,
        globalX: 0,
        globalY: 0,
        width,
        height,
        padding,
        path,
        arrowEnd,
        emphasis,
        strokeWidth,
        annotationId: seed,
      }).strokes,
    [
      shape,
      width,
      height,
      padding,
      path,
      arrowEnd,
      emphasis,
      strokeWidth,
      seed,
    ],
  );

  const durationMs = target.transitionDurationMs ?? 200;
  const trace = !!target.animateTrace;
  const traceProgress = useSketchReveal({
    key: `${seed}:${target.seq}`,
    durationMs,
    enabled: trace && !snap,
  });
  // Heads flick on only when this mounted mark gains its arrow end; a window
  // that mounts the finished arrow (a new display hydrating) shows it whole.
  const [drewShaftFirst, setDrewShaftFirst] = useState(!arrowEnd);
  if (!arrowEnd && !drewShaftFirst) setDrewShaftFirst(true);
  const headProgress = useSketchReveal({
    key: `${seed}:heads`,
    durationMs: ARROW_HEAD_REVEAL_MS,
    enabled: arrowEnd && !trace && drewShaftFirst,
  });
  const progress = target.drawProgress ?? traceProgress;
  const separateHeads = arrowEnd && !trace;

  const outlines = useMemo(() => {
    const body = separateHeads ? strokes.slice(0, 1) : strokes;
    const heads = separateHeads ? strokes.slice(1) : [];
    return [
      ...sketchStrokePaths(body, progress).map((d, index) => ({
        d,
        alpha: body[index]!.alpha ?? INK_ALPHA,
        part: index === 0 ? "shaft" : "head",
      })),
      ...sketchStrokePaths(heads, headProgress).map((d, index) => ({
        d,
        alpha: heads[index]!.alpha ?? INK_ALPHA,
        part: "head",
      })),
    ];
  }, [strokes, progress, headProgress, separateHeads]);

  // Only enclosing shapes take a wash; the marker and underline are open.
  const washable = !isPath && shape !== "marker" && shape !== "underline";
  const wash =
    washable && (target.fillOpacity ?? 0) > 0 && progress >= 1 && strokes[0]
      ? outlineSvgPath(strokes[0].points)
      : null;
  const transitionTimingFunction =
    target.transition === "linear" ? "linear" : "cubic-bezier(0.4, 0, 0.2, 1)";

  return (
    <svg
      data-annotation-id={target.annotationId}
      data-annotation-shape={shape}
      data-testid={testId}
      onTransitionEnd={(event) => {
        if (event.propertyName === "opacity" && visible) onOpacitySettled();
      }}
      className="pointer-events-none fixed overflow-hidden"
      style={{
        left: geometry.left,
        top: geometry.top,
        width: geometry.width,
        height: geometry.height,
        zIndex: 9998,
        opacity: visible ? 1 : 0,
        transitionProperty: snap ? undefined : "opacity",
        transitionDuration: `${durationMs}ms`,
        transitionTimingFunction,
      }}
      viewBox={`0 0 ${geometry.width} ${geometry.height}`}
    >
      <g
        transform={`translate(${geometry.contentLeft} ${geometry.contentTop})`}
      >
        {wash ? (
          <path
            d={wash}
            fill={ink}
            fillOpacity={target.fillOpacity}
            data-sketch-wash="true"
          />
        ) : null}
        {outlines.map((outline, index) => (
          <path
            key={index}
            d={outline.d}
            fill={ink}
            fillOpacity={outline.alpha}
            data-sketch-stroke={outline.part}
          />
        ))}
      </g>
    </svg>
  );
});
